import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { ChevronsRightIcon, Download } from "lucide-react";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Pagination } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";
import * as XLSX from "xlsx";
import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";

const Districts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState(null);

  const [search, setSearch] = useState("");
  const [districts, setDistricts] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);

  const fetchDistricts = async () => {
    try {
      setLoader(true);
      const queryParams = {
        search: search || undefined,
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };

      const response = await fetchData(
        `all-districts`,
        "GET",
        null,
        {},
        queryParams,
        navigate,
        "/districts",
        dispatch,
        "districts"
      );
      setLoader(false);
      if (response?.success === false || !response?.data) {
        setDistricts([]);
        setPagination({
          currentPage: 1,
          pageSize: pagination.pageSize,
          totalItems: 0,
          totalPages: 0,
        });
      } else {
        setDistricts(response?.data || []);
        setPagination(response?.pagination || {});
      }
    } catch (error) {
      setLoader(false);
      console.error("Error fetching districts:", error);
      setDistricts([]);
      setPagination({
        currentPage: 1,
        pageSize: pagination.pageSize,
        totalItems: 0,
        totalPages: 0,
      });
    }
  };

  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      const queryParams = {
        search: search || undefined,
        page: 1,
        limit: 10000,
      };

      const response = await fetchData(
        `all-districts`,
        "GET",
        null,
        {},
        queryParams,
        navigate,
        "/districts",
        dispatch,
        "districts"
      );
      if (
        response?.success === false ||
        !response?.data ||
        response?.data.length === 0
      ) {
        toast.warning("No districts found to export");
        return;
      }

      const allDistricts = response?.data || [];
      const excelData = allDistricts.map((district, index) => ({
        "Sr. No": index + 1,
        "District Name": district.District_Name || district.district_name || "",
        Status:
          (district.Status ?? district.status) === 1 ? "Active" : "Inactive",
        "Created At": district.created_at
          ? new Date(district.created_at).toLocaleDateString()
          : "",
        "Updated At": district.updated_at
          ? new Date(district.updated_at).toLocaleDateString()
          : "",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      XLSX.utils.book_append_sheet(workbook, worksheet, "Districts");
      const fileName = `districts_export_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(
        `Successfully exported ${allDistricts.length} districts to Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export districts to Excel");
    } finally {
      setIsExporting(false);
    }
  };
  useEffect(() => {
    fetchDistricts();
  }, [pagination.currentPage, pagination.pageSize, search]);

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handleDelete = async () => {
    setModalVisible(false);

    try {
      setLoader(true);

      const response = await fetchData(
        `districts`,
        "DELETE",
        {
          District_Id: selectedUuid,
          district_id: selectedUuid,
        },
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );

      setLoader(false);

      if (
        response?.success === true ||
        response?.message?.includes("successfully")
      ) {
        setDistricts((prev) =>
          prev.filter(
            (district) =>
              district.District_Id !== selectedUuid &&
              district.district_id !== selectedUuid
          )
        );

        setPagination((prev) => ({
          ...prev,
          totalItems: prev.totalItems - 1,
          totalPages: Math.ceil((prev.totalItems - 1) / prev.pageSize),
        }));

        toast.success("District deleted successfully.");
      } else {
        await fetchDistricts();
        toast.error(response?.message || "Failed to delete the district.");
      }
    } catch (error) {
      setLoader(false);
      console.error("Error deleting district:", error);

      toast.error(
        error?.message ||
          error?.response?.data?.message ||
          "An error occurred while deleting the district."
      );

      await fetchDistricts();
    }
  };

  const showDeleteModal = (District_Id) => {
    setSelectedUuid(District_Id);
    setModalVisible(true);
  };

  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
      sortable: false,
    },
    {
      name: <span className="font-[Inter] text-[0.813rem]">District Name</span>,
      selector: (row) => row.District_Name || row.district_name,
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      cell: (row) => {
        const status = row.Status || row.status;
        if (status === null || status === undefined) {
          return <span className="text-gray-400">-</span>;
        }

        const isActive = status === 1;

        return (
          <span
            className={`py-1 px-2 text-[0.85em] rounded ${
              isActive ? "bg-success text-white" : "bg-red-500 text-white"
            }`}
          >
            {isActive ? "Active" : "In-Active"}
          </span>
        );
      },
    },
  ];
  {
    if (
      permissions.includes("district-update") ||
      permissions.includes("district-delete")
    ) {
      columns.push({
        name: <span className="text-[0.813rem]">Action</span>,
        cell: (row) => (
          <div className="flex gap-2">
            {/* <Link to={`/districts/edit/${row.District_Id || row.district_id}`}>
              <i className="fa-solid fa-pen-to-square fa-lg"></i>
            </Link>
            <button
              onClick={() => showDeleteModal(row.District_Id || row.district_id)}
            >
              <i
                className="fa fa-trash fa-lg text-primaryDark"
                aria-hidden="true"
              ></i>
            </button> */}

            <PermissionButton
              hasPermission={permissions.includes("district-update")}
              to={`/districts/edit/${row.district_id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon={true}
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed"
            />
            <PermissionButton
              hasPermission={permissions.includes("district-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row.District_Id)}
              className="p-1 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />
          </div>
        ),
      });
    }
  }

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            District
          </h1>
          <div className="flex gap-3">
            {districts.length > 0 && (
              <button
                onClick={exportToExcel}
                disabled={isExporting}
                className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                  isExporting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Download size={16} />
                {isExporting ? "Exporting..." : "Export Excel"}
              </button>
            )}
            {/* <Link to="add">
              <AddButton text="Add" icon={ChevronsRightIcon} />
            </Link> */}

            <PermissionButton
              hasPermission={permissions.includes("district-create")}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>

        {/* <br /> */}
        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              District List
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />

              <input
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                placeholder="Search..."
                type="search"
                className="text-[Light] flex items-center bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
              />
            </div>

            <DataTable
              columns={columns}
              persistTableHead
              data={districts}
              customStyles={customStyles}
              highlightOnHover
            />
            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={districts.length}
            />
          </div>
        </div>

        <CustomConfirmModal
          isVisible={isModalVisible}
          onConfirm={handleDelete}
          onCancel={() => setModalVisible(false)}
          message="Are you sure you want to delete this district?"
        />
      </div>
    </div>
  );
};

export default Districts;
